import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsDownload, BsQrCode } from 'react-icons/bs';

import CrmCompanyLink from '#crmFeature/components/common/CrmCompanyLink/CrmCompanyLink';
import Button from '#globalComponents/Button/Button';
import EnumFilterControl from '#globalComponents/Table/filters/EnumFilterControl';
import HeaderFilter from '#globalComponents/Table/filters/HeaderFilter';
import YesNoFilterControl from '#globalComponents/Table/filters/YesNoFilterControl';
import Table from '#globalComponents/Table/Table';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import { formatDateWithTime } from '#lib/dateUtils';

import { POST_SEND_STATUS_OPTIONS } from '../../../constants/postMailingSendFilters';
import { useDownloadPostMailingSendPdf } from '../../../models/campaigns/mutations';

import PostMailingSendStatusBadge from './PostMailingSendStatusBadge';

// Statuses at which a letter PDF has been rendered and (best-effort) persisted.
const PDF_AVAILABLE_STATUSES = new Set(['pdf_ready', 'processing', 'at_printer', 'printed', 'sent', 'returned']);

const BACK_TO_STATISTICS_LABEL = 'Zurück zur Statistik';

const formatAddress = (address) => {
	if (!address) return '-';
	const line2 = [address.postal, address.city].filter(Boolean).join(' ');
	return [address.street, line2].filter(Boolean).join(', ') || '-';
};

const PostMailingSendsTable = ({
	sends,
	campaignId,
	showQrColumn = false,
	filters,
	onFilterChange,
	statusCounts,
	...tableProps
}) => {
	const [downloadingSendId, setDownloadingSendId] = useState(null);
	const downloadPdf = useDownloadPostMailingSendPdf();
	const { hasCapability: hasCrm } = useCapabilityCheck('crm.access');

	const handleDownload = (sendId) => {
		setDownloadingSendId(sendId);
		downloadPdf.mutate({ campaignId, sendId }, { onSettled: () => setDownloadingSendId(null) });
	};

	const columns = [
		{
			key: 'recipientName',
			label: 'Empfänger',
			render: (send) =>
				hasCrm && send.crmCompanyId ? (
					<CrmCompanyLink
						companyId={send.crmCompanyId}
						contactId={send.crmContactId}
						backLabel={BACK_TO_STATISTICS_LABEL}
					>
						{send.recipientName || '-'}
					</CrmCompanyLink>
				) : (
					<span className="text-text">{send.recipientName || '-'}</span>
				),
		},
		{
			key: 'companyName',
			label: 'Firma',
			minWidth: 160,
			render: (send) => <span className="block max-w-xs truncate">{send.companyName || '-'}</span>,
		},
		{
			key: 'recipientAddress',
			label: 'Adresse',
			render: (send) => <span>{formatAddress(send.recipientAddress)}</span>,
		},
		{
			key: 'status',
			label: 'Status',
			headerAccessory: (
				<HeaderFilter label="Status filtern" active={Boolean(filters.status)}>
					<EnumFilterControl
						heading="Status"
						paramKey="status"
						options={POST_SEND_STATUS_OPTIONS}
						value={filters.status}
						counts={statusCounts}
						onChange={onFilterChange}
					/>
				</HeaderFilter>
			),
			render: (send) => <PostMailingSendStatusBadge status={send.status} />,
		},
		{
			key: 'sentAt',
			label: 'Bei der Post',
			render: (send) => (
				<span className="text-bgBase-800">{send.sentAt ? formatDateWithTime(send.sentAt) : '-'}</span>
			),
		},
		...(showQrColumn
			? [
					{
						key: 'qrScanAt',
						label: 'QR gescannt',
						headerAccessory: (
							<YesNoFilterControl
								heading="QR gescannt"
								paramKey="scanned"
								value={filters.scanned}
								hint="„Nein“ zeigt nur Briefe, die bei der Post sind."
								onChange={onFilterChange}
							/>
						),
						render: (send) =>
							send.qrScanAt ? (
								<span className="inline-flex items-center gap-1.5 text-success-700">
									<BsQrCode /> {formatDateWithTime(send.qrScanAt)}
								</span>
							) : (
								<span className="text-bgBase-800">-</span>
							),
					},
				]
			: []),
		{
			key: 'pdf',
			label: 'PDF',
			align: 'right',
			render: (send) => {
				const canDownload = send.hasPdf && PDF_AVAILABLE_STATUSES.has(send.status);
				const isDownloading = downloadingSendId === send.id;
				return (
					<Button
						variant="outline"
						size="sm"
						onClick={() => handleDownload(send.id)}
						disabled={!canDownload || isDownloading}
						loading={isDownloading}
						title={canDownload ? 'PDF herunterladen' : 'Kein PDF verfügbar'}
					>
						{!isDownloading && <BsDownload className="h-4 w-4" />}
						PDF
					</Button>
				);
			},
		},
	];

	return <Table layoutKey="postSends" rows={sends} columns={columns} {...tableProps} />;
};

PostMailingSendsTable.propTypes = {
	sends: PropTypes.arrayOf(PropTypes.object).isRequired,
	campaignId: PropTypes.string.isRequired,
	showQrColumn: PropTypes.bool,
	filters: PropTypes.shape({
		status: PropTypes.arrayOf(PropTypes.string),
		scanned: PropTypes.bool,
		search: PropTypes.string,
	}).isRequired,
	onFilterChange: PropTypes.func.isRequired,
	/** Letters per status option, shown in the status filter. */
	statusCounts: PropTypes.objectOf(PropTypes.number),
};

export default PostMailingSendsTable;
